import { animate, useMotionValue, useTransform } from 'framer-motion';
import {
  MouseEvent,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';

const SWIPE_START_PX = 8;
const SCROLL_CANCEL_PX = 10;
const OVERDRAG_PX = 24;
const OPEN_THRESHOLD_SHARE = 1 / 3;
const CLICK_AFTER_SWIPE_MS = 350;
const SNAP_TRANSITION = { duration: 0.24, ease: [0.2, 0.7, 0.2, 1] } as const;

export enum SwipeIntent {
  Pending = 'pending',
  Scroll = 'scroll',
  Swipe = 'swipe',
}

export const resolveSwipeIntent = (dx: number, dy: number): SwipeIntent => {
  if (Math.abs(dy) > SCROLL_CANCEL_PX && Math.abs(dy) > Math.abs(dx)) {
    return SwipeIntent.Scroll;
  }
  if (Math.abs(dx) < SWIPE_START_PX) return SwipeIntent.Pending;
  return SwipeIntent.Swipe;
};

export const clampSwipeOffset = (offset: number, trayWidth: number) =>
  Math.max(-trayWidth - OVERDRAG_PX, Math.min(0, offset));

export const shouldOpenOnRelease = (offset: number, trayWidth: number) =>
  offset < -trayWidth * OPEN_THRESHOLD_SHARE;

/**
 * A left swipe that drags the card over a tray of `trayWidth` px and settles it open or closed.
 * Hand-rolled on pointer events rather than framer's `drag`: its start threshold is fixed, while
 * the swipe must wait for 8px sideways and give up to a scroll after 10px vertical.
 */
export const useCardSwipe = ({
  isEnabled,
  isOpen,
  trayWidth,
  onOpenChange,
  onSwipeStart,
}: {
  isEnabled: boolean;
  isOpen: boolean;
  trayWidth: number;
  onOpenChange: (isOpen: boolean) => void;
  onSwipeStart?: () => void;
}) => {
  const offset = useMotionValue(isOpen ? -trayWidth : 0);
  const trayVisibleWidth = useTransform(offset, (value) => -value);

  // Gesture bookkeeping lives in refs: it changes on every pointermove and nothing renders from it.
  const isSwipingRef = useRef(false);
  const swallowClicksUntilRef = useRef(0);
  const stopGestureRef = useRef<(() => void) | null>(null);

  // the open state can also change from outside (another card opened, a tap elsewhere)
  useEffect(() => {
    const controls = animate(offset, isOpen ? -trayWidth : 0, SNAP_TRANSITION);
    return () => controls.stop();
  }, [isOpen, offset, trayWidth]);

  useEffect(() => () => stopGestureRef.current?.(), []);

  const settle = useCallback(
    (open: boolean) => {
      animate(offset, open ? -trayWidth : 0, SNAP_TRANSITION);
      if (open !== isOpen) onOpenChange(open);
    },
    [isOpen, offset, onOpenChange, trayWidth],
  );

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (!isEnabled || event.button > 0) return;

    stopGestureRef.current?.();
    const { pointerId, clientX: startX, clientY: startY } = event;
    const startOffset = isOpen ? -trayWidth : 0;

    const handleMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;

      const dx = moveEvent.clientX - startX;

      if (!isSwipingRef.current) {
        const intent = resolveSwipeIntent(dx, moveEvent.clientY - startY);
        if (intent === SwipeIntent.Scroll) {
          stopGesture();
          return;
        }
        if (intent === SwipeIntent.Pending) return;

        isSwipingRef.current = true;
        offset.stop();
        onSwipeStart?.();
      }

      offset.set(clampSwipeOffset(startOffset + dx, trayWidth));
    };

    const handleEnd = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== pointerId) return;

      const wasSwiping = isSwipingRef.current;
      stopGesture();
      if (!wasSwiping) return;

      swallowClicksUntilRef.current = Date.now() + CLICK_AFTER_SWIPE_MS;
      settle(shouldOpenOnRelease(offset.get(), trayWidth));
    };

    function stopGesture() {
      isSwipingRef.current = false;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
      stopGestureRef.current = null;
    }

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
    stopGestureRef.current = stopGesture;
  };

  // the click a swipe leaves behind, or a tap on the open card, only closes it
  const onClickCapture = (event: MouseEvent<HTMLElement>) => {
    const isSwipeLeftover = Date.now() < swallowClicksUntilRef.current;
    if (!isSwipeLeftover && !isOpen) return;

    event.preventDefault();
    event.stopPropagation();
    if (!isSwipeLeftover) settle(false);
  };

  const isSwiping = useCallback(() => isSwipingRef.current, []);

  return {
    offset,
    trayVisibleWidth,
    isSwiping,
    close: () => settle(false),
    cardProps: { onPointerDown, onClickCapture },
  };
};
